/**
 * SEO, GEO (Generative Engine Optimization), and AEO (Answer Engine Optimization) engine.
 * Handles client-side metadata, Open Graph, Twitter cards, Canonical links, Geo tags,
 * and JSON-LD structured data (schema.org).
 */

import { getCachedPublicDocumentTitle, DEFAULT_PUBLIC_DOCUMENT_TITLE } from './siteTitleDefaults';

export const SITE_NAME = 'Invincible NGO';
export const SITE_TAGLINE = "India's Largest Trekking Organisation";
export const DEFAULT_PUBLIC_URL = 'https://invinciblengo.org';

export function stripHtmlForSeo(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function upsertMeta(attr, key, content) {
  if (typeof document === 'undefined' || content == null || content === '') return;
  const selector =
    attr === 'name'
      ? `meta[name="${key}"]`
      : `meta[property="${key}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    if (attr === 'name') el.setAttribute('name', key);
    else el.setAttribute('property', key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', String(content));
}

export function upsertCanonical(href) {
  if (typeof document === 'undefined' || !href) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function upsertJsonLd(scriptId, jsonLdData) {
  if (typeof document === 'undefined') return;
  const id = scriptId || 'page-schema-jsonld';
  let el = document.getElementById(id);
  if (!jsonLdData) {
    if (el && el.parentNode) el.parentNode.removeChild(el);
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('id', id);
    document.head.appendChild(el);
  }
  try {
    el.textContent = typeof jsonLdData === 'string' ? jsonLdData : JSON.stringify(jsonLdData);
  } catch {
    /* ignore json serialization errors */
  }
}

export function removeJsonLd(scriptId) {
  if (typeof document === 'undefined') return;
  const id = scriptId || 'page-schema-jsonld';
  const el = document.getElementById(id);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/**
 * Applies all SEO, OpenGraph, Twitter, Geo, and JSON-LD schema tags to the page.
 * @param {{
 *   title?: string,
 *   description?: string,
 *   canonical?: string,
 *   image?: string,
 *   siteName?: string,
 *   type?: string,
 *   keywords?: string,
 *   robots?: string,
 *   schema?: object | array,
 *   schemaId?: string
 * }} opts
 */
export function applyPageSeo(opts = {}) {
  if (typeof document === 'undefined') return () => {};

  const title = String(opts.title || '').trim() || getCachedPublicDocumentTitle() || SITE_NAME;
  const description = String(opts.description || '').trim();
  const siteName = opts.siteName || SITE_NAME;
  const canonical = opts.canonical || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : '');
  const image = opts.image || `${DEFAULT_PUBLIC_URL}/assets/og-image.jpg`;
  const schemaId = opts.schemaId || 'page-schema-jsonld';

  document.title = title;

  // Standard Metadata
  if (description) upsertMeta('name', 'description', description);
  if (opts.keywords) upsertMeta('name', 'keywords', opts.keywords);
  upsertMeta('name', 'robots', opts.robots || 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
  upsertMeta('name', 'author', 'Invincible NGO');

  // Geo Metadata
  upsertMeta('name', 'geo.region', 'IN-GJ');
  upsertMeta('name', 'geo.placename', 'Ahmedabad, Gujarat, India');
  upsertMeta('name', 'geo.position', '23.0371;72.5485');
  upsertMeta('name', 'ICBM', '23.0371, 72.5485');

  // Open Graph
  upsertMeta('property', 'og:title', title);
  if (description) upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:site_name', siteName);
  upsertMeta('property', 'og:type', opts.type || 'website');
  if (canonical) upsertMeta('property', 'og:url', canonical);
  if (image) upsertMeta('property', 'og:image', image);
  upsertMeta('property', 'og:locale', 'en_IN');

  // Twitter / X Cards
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:site', '@invincible_ngo');
  upsertMeta('name', 'twitter:title', title);
  if (description) upsertMeta('name', 'twitter:description', description);
  if (image) upsertMeta('name', 'twitter:image', image);

  // Canonical
  if (canonical) upsertCanonical(canonical);

  // Structured Data (JSON-LD)
  if (opts.schema) {
    upsertJsonLd(schemaId, opts.schema);
  }

  return () => {
    document.title = getCachedPublicDocumentTitle() || DEFAULT_PUBLIC_DOCUMENT_TITLE;
    removeJsonLd(schemaId);
  };
}

export function buildTrekPageSeo(trek, siteSettings = {}) {
  const brand = String(siteSettings.site_brand_name || '').trim() || SITE_NAME;
  const name = String(trek?.name || trek?.title || '').trim() || 'Trek';
  const rawMeta = String(trek?.metaTitle || '').trim();
  const metaIsGeneric =
    !rawMeta ||
    /^trek$/i.test(rawMeta) ||
    /^trek\s*\|/i.test(rawMeta);
  const title = metaIsGeneric
    ? `${name}${/trek|expedition|camp/i.test(name) ? '' : ' Trek'} | ${brand}`
    : rawMeta;
  const description =
    String(trek?.metaDescription || '').trim() ||
    String(trek?.tagline || '').trim() ||
    stripHtmlForSeo(trek?.description).slice(0, 160) ||
    String(siteSettings.site_meta_description || '').trim();
  return { title, description };
}

/* ==========================================================================
   SCHEMA BUILDERS (schema.org) FOR GEO & AEO
   ========================================================================== */

export function buildOrganizationSchema(siteSettings = {}) {
  const brand = siteSettings.site_brand_name || SITE_NAME;
  const phone = siteSettings.contact_phone ? String(siteSettings.contact_phone).split('\n')[0].trim() : '+919099400699';
  const email = siteSettings.contact_email ? String(siteSettings.contact_email).split('\n')[0].trim() : 'info@invinciblengo.org';
  const address = siteSettings.contact_address || '308, University Plaza, Above Chocolate Room, Vijay Cross Roads, Navrangpura, Ahmedabad, Gujarat 380009';

  return {
    '@context': 'https://schema.org',
    '@type': 'NGO',
    '@id': `${DEFAULT_PUBLIC_URL}/#organization`,
    name: brand,
    alternateName: 'Invincible The NGO',
    url: DEFAULT_PUBLIC_URL,
    logo: `${DEFAULT_PUBLIC_URL}/assets/logo.png`,
    description: "India's largest youth trekking and adventure organisation conducting affordable Himalayan expeditions, nature camps, and youth leadership initiatives.",
    foundingDate: '2013',
    address: {
      '@type': 'PostalAddress',
      streetAddress: address,
      addressLocality: 'Ahmedabad',
      addressRegion: 'Gujarat',
      postalCode: '380009',
      addressCountry: 'IN'
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: phone,
        email: email,
        contactType: 'customer support',
        areaServed: 'IN',
        availableLanguage: ['English', 'Hindi', 'Gujarati']
      }
    ],
    sameAs: [
      siteSettings.social_instagram || 'https://www.instagram.com/invincible_ngo/',
      siteSettings.social_facebook || 'https://www.facebook.com/invincible.ngo',
      siteSettings.social_youtube || 'https://www.youtube.com/@Invinciblengo',
      siteSettings.social_linkedin || 'https://www.linkedin.com/company/invincible-ngo'
    ]
  };
}

export function buildWebSiteSchema(siteSettings = {}, publicBase = DEFAULT_PUBLIC_URL) {
  const base = (publicBase || DEFAULT_PUBLIC_URL).replace(/\/$/, '');
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${base}/#website`,
    url: base,
    name: siteSettings.site_brand_name || SITE_NAME,
    description: siteSettings.site_meta_description || "Affordable trekking and adventure camps across India.",
    publisher: {
      '@id': `${base}/#organization`
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${base}/events?search={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };
}

export function buildBreadcrumbSchema(items = [], publicBase = DEFAULT_PUBLIC_URL) {
  const base = (publicBase || DEFAULT_PUBLIC_URL).replace(/\/$/, '');
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${base}${item.url.startsWith('/') ? item.url : `/${item.url}`}`
    }))
  };
}

export function buildFaqSchema(faqs = []) {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;
  const validFaqs = faqs.filter(f => (f.question || f.q) && (f.answer || f.a));
  if (validFaqs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: validFaqs.map(f => ({
      '@type': 'Question',
      name: stripHtmlForSeo(f.question || f.q),
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripHtmlForSeo(f.answer || f.a)
      }
    }))
  };
}

export function buildTouristTripSchema(trek, batches = [], siteSettings = {}, publicBase = DEFAULT_PUBLIC_URL) {
  if (!trek) return null;
  const base = (publicBase || DEFAULT_PUBLIC_URL).replace(/\/$/, '');
  const slug = trek.slug || '';
  const url = `${base}/events/${slug}`;
  const name = trek.name || trek.title || 'Trek';
  const description = stripHtmlForSeo(trek.description || trek.tagline || trek.shortDescription);
  const mediaList = Array.isArray(trek.media) ? trek.media : [];
  const images = mediaList.map(m => m.url).filter(Boolean);
  if (trek.imageUrl) images.unshift(trek.imageUrl);

  // Price & Offers
  let price = null;
  if (trek.price) price = trek.price;
  else if (Array.isArray(trek.packages) && trek.packages[0]?.price) price = trek.packages[0].price;

  const validBatches = Array.isArray(batches) ? batches.filter(b => b.startDate) : [];
  const offers = validBatches.length > 0 ? validBatches.slice(0, 10).map(b => ({
    '@type': 'Offer',
    url: `${url}?date=${encodeURIComponent(b.startDate)}`,
    priceCurrency: 'INR',
    price: b.price || price || 0,
    validFrom: b.startDate,
    availability: b.availableSeats > 0 ? 'https://schema.org/InStock' : 'https://schema.org/LimitedAvailability'
  })) : price ? {
    '@type': 'Offer',
    url: url,
    priceCurrency: 'INR',
    price: price,
    availability: 'https://schema.org/InStock'
  } : undefined;

  // Itinerary
  const itinerary = Array.isArray(trek.scheduleDays) ? trek.scheduleDays.map(day => ({
    '@type': 'TouristTrip',
    name: `Day ${day.dayNumber || day.day || ''}: ${day.title || 'Trek Day'}`,
    description: stripHtmlForSeo(day.description || day.activities)
  })) : undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    '@id': `${url}#trip`,
    name: name,
    description: description,
    url: url,
    image: images.length > 0 ? images : undefined,
    touristType: ['Trekkers', 'Adventure Seekers', 'Youth', 'Nature Enthusiasts'],
    offers: offers,
    provider: {
      '@type': 'NGO',
      name: siteSettings.site_brand_name || SITE_NAME,
      url: base
    },
    itinerary: itinerary
  };
}

export function buildArticleSchema(blog, siteSettings = {}, publicBase = DEFAULT_PUBLIC_URL) {
  if (!blog) return null;
  const base = (publicBase || DEFAULT_PUBLIC_URL).replace(/\/$/, '');
  const url = `${base}/blogs/${blog.slug || ''}`;
  const title = blog.title || 'Blog Post';
  const description = stripHtmlForSeo(blog.shortDescription || blog.metaDescription || blog.content).slice(0, 200);
  const image = blog.coverImageUrl || blog.imageUrl;
  const published = blog.publishedAt || blog.createdAt || new Date().toISOString();
  const modified = blog.updatedAt || published;

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url
    },
    headline: title,
    description: description,
    image: image ? [image] : undefined,
    datePublished: published,
    dateModified: modified,
    author: {
      '@type': 'Person',
      name: blog.author || siteSettings.site_brand_name || SITE_NAME
    },
    publisher: {
      '@type': 'NGO',
      name: siteSettings.site_brand_name || SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${base}/assets/logo.png`
      }
    }
  };
}

export function buildJobPostingSchema(job, siteSettings = {}, publicBase = DEFAULT_PUBLIC_URL) {
  if (!job) return null;
  const base = (publicBase || DEFAULT_PUBLIC_URL).replace(/\/$/, '');
  const title = job.title || 'Position';
  const description = stripHtmlForSeo(job.description || job.responsibilities || 'Join our adventure organisation team.');
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: title,
    description: description,
    datePosted: job.createdAt || new Date().toISOString(),
    hiringOrganization: {
      '@type': 'NGO',
      name: siteSettings.site_brand_name || SITE_NAME,
      sameAs: base,
      logo: `${base}/assets/logo.png`
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: job.location || 'Ahmedabad',
        addressRegion: 'Gujarat',
        addressCountry: 'IN'
      }
    },
    employmentType: job.employmentType || 'FULL_TIME'
  };
}

